/**
 * Local plain-string i18n helpers replacing `@eventbrite/i18n`.
 *
 * The legacy-infra i18n shim's `gettext` returns a lazy `GenericLazyString`
 * (unsafe to render in React 19) and has no `ngettext` — per memory.md the
 * sanctioned pattern is a tiny local `gettext`/`ngettext` returning plain
 * strings with `%(name)s` interpolation. A consuming app that localizes wires
 * its catalog via `setMessages` (resolved in gSSP via `webapp-locales`).
 */

type InterpolationValues = Record<string, string | number>;

let activeMessages: Record<string, string> = {};

/** Install a translation catalog (msgid → translated template). */
export const setMessages = (messages: Record<string, string>) => {
    activeMessages = messages ?? {};
};

const interpolate = (template: string, values?: InterpolationValues): string =>
    values
        ? template.replace(/%\((\w+)\)s/g, (match, key: string) =>
              key in values ? String(values[key]) : match,
          )
        : template;

export const gettext = (
    message: string,
    values?: InterpolationValues,
): string => interpolate(activeMessages[message] ?? message, values);

export const ngettext = (
    singular: string,
    plural: string,
    count: number,
    values?: InterpolationValues,
): string => {
    const template =
        count === 1
            ? activeMessages[singular] ?? singular
            : activeMessages[plural] ?? plural;
    return interpolate(template, values);
};
